import { authErrorMessage } from "@auth/authErrorMessage";
import { deriveSubdomainFromLocation } from "@auth/subdomain";
import { AuthSheet } from "@components/AuthSheet";
import { useAuth } from "@context/AuthContext";
import { DISPLAY_PATH } from "@displays/displayPath";
import { Button, Input } from "@scribenote/brian-design-system";
import { useEffect, useRef, useState, type SubmitEvent } from "react";

const GENERIC_ERROR = "Incorrect password. Please try again.";

export const LoginScreen = () => {
  const { login } = useAuth();
  // Localhost has no subdomain, so the form collects it.
  const derivedSubdomain = deriveSubdomainFromLocation();
  const needsSubdomain = derivedSubdomain === null;

  const [subdomain, setSubdomain] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const subdomainRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    (needsSubdomain ? subdomainRef : passwordRef).current?.focus();
  }, [needsSubdomain]);

  const effectiveSubdomain = (derivedSubdomain ?? subdomain)
    .trim()
    .toLowerCase();

  const handleSubmit = async (e: SubmitEvent) => {
    e.preventDefault();
    if (!effectiveSubdomain || !password || submitting) return;
    setSubmitting(true);
    try {
      await login(effectiveSubdomain, password);
    } catch (err) {
      setError(authErrorMessage(err, GENERIC_ERROR));
      setPassword("");
      passwordRef.current?.focus();
    } finally {
      setSubmitting(false);
    }
  };

  const clearError = () => {
    if (error) setError(null);
  };

  return (
    <AuthSheet>
      <div className="text-center">
        <h1 className="font-display text-xl font-medium tracking-tight text-ink">
          Sign in to Brian
        </h1>
        <p className="mt-1 text-sm text-ink-body">
          I just need your clinic password.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="mt-6 flex flex-col gap-4">
        {needsSubdomain && (
          <Input
            ref={subdomainRef}
            id="clinic-subdomain"
            type="text"
            label="Clinic subdomain"
            autoComplete="off"
            value={subdomain}
            onChange={(e) => {
              setSubdomain(e.target.value);
              clearError();
            }}
            placeholder="demo"
            disabled={submitting}
            data-testid="login-subdomain"
          />
        )}
        <Input
          ref={passwordRef}
          id="clinic-password"
          type="password"
          label="Clinic password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
            clearError();
          }}
          placeholder="••••"
          error={error ?? undefined}
          disabled={submitting}
          data-testid="login-password"
        />

        <Button
          type="submit"
          variant="primary"
          busy={submitting}
          disabled={!effectiveSubdomain || !password}
          data-testid="login-submit"
          className="mt-1 w-full"
        >
          {submitting ? "Signing in…" : "Sign in"}
        </Button>
      </form>

      <a
        href={DISPLAY_PATH}
        data-testid="login-setup-display"
        className="mt-6 block rounded-[2px] text-center text-sm text-annotation underline-offset-4 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
      >
        Set up this screen as a display
      </a>
    </AuthSheet>
  );
};
